import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, X, Maximize2, Camera, Sparkles } from "lucide-react";
import { publicApi } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import PaymentBadges from "@/components/PaymentBadges";
import HoodieDesigner from "@/components/HoodieDesigner";
import ShareButton from "@/components/ShareButton";
import ProductReviews from "@/components/ProductReviews";
import { trackProductClick } from "@/lib/analytics";

const FALLBACK_SIZES = [
  { value: "A4", label: "A4  ·  21 × 29.7 cm", mult: 1.0 },
  { value: "A3", label: "A3  ·  29.7 × 42 cm", mult: 1.6 },
  { value: "A2", label: "A2  ·  42 × 59.4 cm", mult: 2.4 },
  { value: "A1", label: "A1  ·  59.4 × 84.1 cm", mult: 3.6 },
  { value: "A0", label: "A0  ·  84.1 × 118.9 cm", mult: 5.0 },
];
const FALLBACK_FRAMES = [
  { value: "unframed", label: "Unframed", add: 0 },
  { value: "framed", label: "Framed (satin black)", add: 90 },
];

// Apparel customization options
// Palette C — 9 CKA hoodie colourways (updated 2026-02)
const HOODIE_COLOURS = [
  { value: "pitch_black", label: "Pitch Black", swatch: "#0A0B0F", border: "#0A0B0F" },
  { value: "blue_dusk",   label: "Blue Dusk",   swatch: "#25303B", border: "#25303B" },
  { value: "deep_royal",  label: "Deep Royal",  swatch: "#0F2C74", border: "#0F2C74" },
  { value: "cherry_red",  label: "Cherry Red",  swatch: "#B5222C", border: "#B5222C" },
  { value: "garnet",      label: "Garnet",      swatch: "#7C1E32", border: "#7C1E32" },
  { value: "olive",       label: "Olive",       swatch: "#3E3A1E", border: "#3E3A1E" },
  { value: "gravel",      label: "Gravel",      swatch: "#9BA0A2", border: "#7E8385" },
  { value: "tan",         label: "Tan",         swatch: "#D2A87A", border: "#B98F63" },
  { value: "off_white",   label: "Off White",   swatch: "#F1E9DA", border: "#D6CEBF" },
];
const LOGO_COLOURS = [
  { value: "orange", label: "CKA Orange", swatch: "#FF4500", border: "#FF4500" },
  { value: "black", label: "Black", swatch: "#0A0A0A", border: "#333" },
  { value: "white", label: "White", swatch: "#F5F5F5", border: "#CCC" },
  { value: "red", label: "Red", swatch: "#B01818", border: "#B01818" },
  { value: "yellow", label: "Yellow", swatch: "#F2C94C", border: "#F2C94C" },
  { value: "pink", label: "Pink", swatch: "#F19CBB", border: "#F19CBB" },
  { value: "turquoise", label: "Turquoise", swatch: "#3ABAB4", border: "#3ABAB4" },
  { value: "gray", label: "Gray", swatch: "#808080", border: "#666" },
  { value: "gold", label: "Metallic Gold", swatch: "#C9A227", border: "#C9A227" },
];
// Fallback print designs used only if the /api/hoodie-logos fetch fails.
// The real source of truth is the hoodie_logos collection, managed via Admin.
const FALLBACK_PRINT_DESIGNS = [
  { value: "cka_emblem", label: "CKA Emblem", tag: "30 × 20 cm · signature crest", preview: "/logo-cka_emblem.png", recolourable: false },
  { value: "ock_colour", label: "Our Culture's King · Colour", tag: "Yellow · red · black on outback map", preview: "/logo-ock_colour.png", recolourable: false },
  { value: "cka_brush", label: "Cam's Koori Art · Brush", tag: "Hand-lettered script mark", preview: "/logo-cka_brush.png", recolourable: true },
  { value: "ock_mono", label: "Our Culture's King · Mono", tag: "Outline · one-colour print", preview: "/logo-ock_mono.png", recolourable: true },
  { value: "cka_gold", label: "CKA Gold Crest", tag: "Gold + black art-deco emblem", preview: "/logo-cka_gold.png", recolourable: false },
];
const APPAREL_SIZES = ["S", "M", "L", "XL", "2XL", "3XL"];
// Font styles offered for the "Custom Name" print option
const NAME_FONTS = [
  { value: "bold", label: "Bold Display", family: "'Anton', 'Bebas Neue', sans-serif", weight: 400 },
  { value: "script", label: "Elegant Script", family: "'Great Vibes', cursive", weight: 400 },
  { value: "marker", label: "Marker Pen", family: "'Permanent Marker', cursive", weight: 400 },
  { value: "graffiti", label: "Graffiti", family: "'Bungee', sans-serif", weight: 400 },
  { value: "chalk", label: "Chalkboard", family: "'Rock Salt', cursive", weight: 400 },
];

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ etsy_shop_url: "" });
  const [size, setSize] = useState("A4");
  const [frame, setFrame] = useState("unframed");
  const [hoodieColour, setHoodieColour] = useState("pitch_black");
  const [logoColour, setLogoColour] = useState("orange");
  const [printDesigns, setPrintDesigns] = useState(FALLBACK_PRINT_DESIGNS);
  const [printDesign, setPrintDesign] = useState(FALLBACK_PRINT_DESIGNS[0].value);
  const [customName, setCustomName] = useState("");
  const [customFont, setCustomFont] = useState("bold");
  const [apparelSize, setApparelSize] = useState("L");
  const [basicColour, setBasicColour] = useState(null); // for mens/womens/basic apparel
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [viewMode, setViewMode] = useState("photo"); // "photo" | "design"
  const [designerView, setDesignerView] = useState("front"); // "front" | "back"
  const [placement, setPlacement] = useState({ x: 30, y: 30, size: 25 }); // percentages
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([publicApi.getProduct(id), publicApi.getConfig()])
      .then(([p, c]) => {
        setProduct(p);
        setConfig(c);
        setSelectedImage(p.image_url || (p.images && p.images[0]) || null);
        trackProductClick(id, "detail_page");
      })
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    // Load Cam-managed hoodie logos (admin can add/hide via /admin/hoodie-logos)
    publicApi
      .listHoodieLogos()
      .then((logos) => {
        if (!logos || logos.length === 0) return;
        const mapped = logos.map((l) => ({
          value: l.id,
          label: l.label,
          tag: l.tag || "",
          preview: l.image_url,
          recolourable: !!l.recolourable,
        }));
        setPrintDesigns(mapped);
        setPrintDesign(mapped[0].value);
      })
      .catch(() => {
        // fallback list is already in state
      });
  }, []);

  const nameFont = NAME_FONTS.find((f) => f.value === customFont) || NAME_FONTS[0];
  const CUSTOM_NAME_VALUE = "__custom_name__";
  // Virtual custom-name entry — prepended to the printDesigns list whenever the
  // buyer has typed something. Selecting it renders the typed text as the print.
  const trimmedName = customName.trim();
  const displayDesigns = trimmedName
    ? [
        {
          value: CUSTOM_NAME_VALUE,
          label: `"${trimmedName}"`,
          tag: `Your name · ${nameFont.label}`,
          preview: null,
          recolourable: true,
          isCustomName: true,
        },
        ...printDesigns,
      ]
    : printDesigns;

  const currentDesign =
    displayDesigns.find((d) => d.value === printDesign) || displayDesigns[0];
  const isRecolourable = !!currentDesign?.recolourable;
  const isCustomNameDesign = !!currentDesign?.isCustomName;

  const isPrint = product?.category === "prints";
  const isApparel = product?.category === "apparel";
  const isMens = product?.category === "mens";
  const isWomens = product?.category === "womens";
  // Signature Hoodie is a fixed, curated design — no colour/logo customisation.
  // Only products explicitly named "Custom" or "Standard" hoodie get the full customiser.
  // Every other apparel item (t-shirts, caps, one-off drops) uses a simple size-only picker.
  const isSignatureApparel = isApparel && /signature/i.test(product?.title || "");
  const isCustomApparel = isApparel && /(custom|standard)\s*hoodie/i.test(product?.title || "");
  const isBasicApparel = (isApparel && !isSignatureApparel && !isCustomApparel) || isMens || isWomens;
  const vp = product?.variant_pricing;
  const PRODUCT_COLOURS = useMemo(() => {
    if (vp && Array.isArray(vp.colours) && vp.colours.length) return vp.colours;
    return [];
  }, [vp]);
  const SIZES = useMemo(() => {
    if (vp && Array.isArray(vp.sizes) && vp.sizes.length) {
      return vp.sizes.map((s) => ({ ...s, mult: null }));
    }
    return FALLBACK_SIZES;
  }, [vp]);
  const FRAMES = useMemo(() => {
    if (vp && Array.isArray(vp.frames) && vp.frames.length) return vp.frames;
    return FALLBACK_FRAMES;
  }, [vp]);

  const computedPrice = useMemo(() => {
    if (!product) return 0;
    if (!isPrint) return product.price;
    const s = SIZES.find((x) => x.value === size) || SIZES[0];
    const f = FRAMES.find((x) => x.value === frame) || FRAMES[0];
    const sizePrice = s && "price" in s && s.price ? s.price : product.price * (s?.mult || 1);
    return Math.round(sizePrice + (f?.add || 0));
  }, [product, size, frame, isPrint, SIZES, FRAMES]);

  if (loading) return <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 text-white/50">Loading...</div>;
  if (!product) return <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24"><Link to="/shop" className="btn-ghost">Back to Shop</Link></div>;

  const externalStore = product.external_url || config.etsy_shop_url;

  const addToCart = () => {
    let variantSuffix = "";
    let variantKey = "";
    if (isPrint) {
      variantSuffix = ` — ${size} · ${frame === "framed" ? "Framed" : "Unframed"}`;
      variantKey = `${size}-${frame}`;
    } else if (isCustomApparel) {
      const hc = HOODIE_COLOURS.find((c) => c.value === hoodieColour)?.label || hoodieColour;
      const pd = currentDesign?.label || printDesign;
      const lc = LOGO_COLOURS.find((c) => c.value === logoColour)?.label || logoColour;
      const px = Math.round(placement.x);
      const py = Math.round(placement.y);
      const ps = Math.round(placement.size);
      const designPart = isCustomNameDesign
        ? `Custom name ${pd} · ${nameFont.label} in ${lc}`
        : isRecolourable
        ? `${pd} in ${lc}`
        : pd;
      const placementPart = `${designerView === "front" ? "Front" : "Back"} · pos ${px}%×${py}% · size ${ps}%`;
      variantSuffix = ` — Size ${apparelSize} · ${hc} · ${designPart} · ${placementPart}`;
      variantKey = isCustomNameDesign
        ? `${apparelSize}-${hoodieColour}-name-${trimmedName}-${customFont}-${logoColour}-${designerView}-${px}-${py}-${ps}`
        : `${apparelSize}-${hoodieColour}-${printDesign}-${logoColour}-${designerView}-${px}-${py}-${ps}`;
    } else if (isSignatureApparel || isBasicApparel) {
      variantSuffix = ` — Size ${apparelSize}`;
      variantKey = `${apparelSize}`;
    }
    addItem({
      ...product,
      id: `${product.id}-${variantKey || "default"}`,
      title: product.title + variantSuffix,
      price: computedPrice,
    });
    toast.success("Added to cart");
  };

  return (
    <div data-testid="page-product-detail" className="max-w-[1400px] mx-auto px-5 md:px-10 py-12">
      <Link to="/shop" data-testid="product-back" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm uppercase tracking-widest mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to shop
      </Link>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div>
          {/* Photo / Designer toggle (custom apparel only) */}
          {isCustomApparel && (
            <div data-testid="hoodie-view-toggle" className="grid grid-cols-2 gap-2 mb-3">
              <button
                data-testid="hoodie-view-photo"
                onClick={() => setViewMode("photo")}
                className={`flex items-center justify-center gap-2 py-2.5 text-xs uppercase tracking-widest border transition-colors ${
                  viewMode === "photo"
                    ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                    : "border-white/20 text-white/70 hover:border-white"
                }`}
              >
                <Camera className="w-3.5 h-3.5" /> Product photo
              </button>
              <button
                data-testid="hoodie-view-designer"
                onClick={() => setViewMode("design")}
                className={`flex items-center justify-center gap-2 py-2.5 text-xs uppercase tracking-widest border transition-colors ${
                  viewMode === "design"
                    ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                    : "border-white/20 text-white/70 hover:border-white"
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" /> Design your hoodie
              </button>
            </div>
          )}

          {isCustomApparel && viewMode === "design" ? (
            <HoodieDesigner
              hoodieColour={HOODIE_COLOURS.find((c) => c.value === hoodieColour)?.swatch || "#0A0A0A"}
              logoColour={LOGO_COLOURS.find((c) => c.value === logoColour)?.swatch || "#FF4500"}
              printPreview={currentDesign?.preview}
              designLabel={currentDesign?.label || ""}
              recolourable={isRecolourable}
              customText={isCustomNameDesign ? trimmedName : ""}
              customFontFamily={isCustomNameDesign ? nameFont.family : ""}
              view={designerView}
              onViewChange={setDesignerView}
              placement={placement}
              onPlacementChange={setPlacement}
            />
          ) : (
            <div className="bg-[#141414] aspect-square overflow-hidden relative group cursor-zoom-in" onClick={() => setZoom(true)} data-testid="product-image-zoom-trigger">
              <img data-testid="product-image" src={selectedImage || product.image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute top-3 left-3 bg-[#0A0A0A]/70 backdrop-blur border border-white/20 text-white/90 text-[10px] uppercase tracking-widest px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                <Maximize2 className="w-3 h-3" /> Click to enlarge
              </div>
            </div>
          )}
          {product.images && product.images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3" data-testid="product-thumbnails">
              {product.images.map((url, i) => (
                <button
                  key={url}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setSelectedImage(url)}
                  className={`aspect-square overflow-hidden bg-[#141414] border-2 transition-colors ${
                    (selectedImage || product.image_url) === url ? "border-[#FF4500]" : "border-transparent hover:border-white/40"
                  }`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <p className="overline mb-3">{product.category === "apparel" ? "CamOriginal · Apparel" : product.category}</p>
              <h1 data-testid="product-title" className="font-display text-4xl md:text-6xl leading-none">{product.title}</h1>
            </div>
            <ShareButton
              testid="product-share-button"
              iconOnly
              title={`${product.title} — CamOriginal`}
              text={product.description ? product.description.slice(0, 140) : "Original CKA piece — Barkindji artist Cameron Boyle."}
              url={typeof window !== "undefined" ? window.location.href : "https://camoriginal.com"}
              label={`Share ${product.title}`}
              className="mt-2 shrink-0"
            />
          </div>
          <p data-testid="product-price" className="text-3xl text-[#FF4500] font-semibold mb-8">
            {isPrint && <span className="block text-xs text-white/50 uppercase tracking-widest mb-1">Your selection</span>}
            ${computedPrice.toFixed(2)} <span className="text-sm text-white/50">{product.currency || "AUD"}</span>
          </p>
          <p data-testid="product-description" className="text-white/70 leading-relaxed mb-6 whitespace-pre-line">{product.description}</p>

          {isPrint && (
            <div data-testid="product-options" className="mb-8 space-y-5 border border-white/10 bg-[#141414] p-5">
              <div>
                <label className="overline block mb-2">Size</label>
                <select
                  data-testid="option-size"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  className="cka-input"
                >
                  {SIZES.map((s) => {
                    const sp = "price" in s && s.price ? s.price : Math.round(product.price * (s.mult || 1));
                    return (
                      <option key={s.value} value={s.value}>
                        {s.label}  ·  ${sp}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Framing</label>
                <div className="grid grid-cols-2 gap-3">
                  {FRAMES.map((f) => (
                    <button
                      key={f.value}
                      data-testid={`option-frame-${f.value}`}
                      onClick={() => setFrame(f.value)}
                      className={`p-3 text-sm border transition-colors text-left ${
                        frame === f.value
                          ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                          : "border-white/20 text-white/80 hover:border-white"
                      }`}
                    >
                      <div className="font-display tracking-wider">{f.label}</div>
                      <div className="text-xs text-white/50 mt-1">
                        {f.add === 0 ? "included" : `+ $${f.add}`}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-white/40 pt-2 border-t border-white/10">Final size & frame confirmed at checkout. Pay by PayID — no card fees.</p>
            </div>
          )}

          {(isSignatureApparel || isBasicApparel) && (
            <div data-testid={isSignatureApparel ? "product-signature-apparel-options" : "product-basic-apparel-options"} className="mb-8 space-y-5 border border-white/10 bg-[#141414] p-5">
              <p className="overline">{isSignatureApparel ? "Signature Hoodie · fixed design" : isMens ? "Men's fit · pick your colour and size" : isWomens ? "Women's fit · pick your colour and size" : "Ready-to-ship apparel"}</p>

              {/* Colour picker — only when the product carries a palette */}
              {PRODUCT_COLOURS.length > 0 && (
                <div data-testid="product-colour-picker">
                  <label className="overline block mb-2">
                    Colour <span className="text-white/40 normal-case tracking-normal">— {basicColour ? PRODUCT_COLOURS.find((c) => c.value === basicColour)?.label : "select one"}</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PRODUCT_COLOURS.map((c) => {
                      const active = basicColour === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          data-testid={`option-colour-${c.value}`}
                          title={c.label}
                          aria-label={c.label}
                          aria-pressed={active}
                          onClick={() => setBasicColour(c.value)}
                          className={`w-10 h-10 rounded-full border-2 transition-all ${
                            active ? "border-[#FF4500] scale-110" : "border-white/20 hover:border-white/60"
                          }`}
                          style={{ background: c.swatch }}
                        >
                          <span className="sr-only">{c.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="overline block mb-2">Size</label>
                <div className="grid grid-cols-6 gap-2">
                  {APPAREL_SIZES.map((s) => (
                    <button
                      key={s}
                      data-testid={`option-apparel-size-${s}`}
                      onClick={() => { setApparelSize(s); setViewMode("design"); }}
                      className={`py-2 text-sm font-display tracking-wider border transition-colors ${
                        apparelSize === s
                          ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                          : "border-white/20 text-white/80 hover:border-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-white/40 pt-2 border-t border-white/10">
                {isSignatureApparel
                  ? "The Signature Hoodie ships in its original CKA colourway and print — no customisation. Want custom colours or placement? Grab a Standard Hoodie instead."
                  : PRODUCT_COLOURS.length > 0
                  ? "Pick a colour and size, then add to cart — each piece is screen-printed to order in the CKA studio."
                  : "This piece ships as shown — pick your size and add to cart. For a fully-customisable hoodie, check out the Standard Hoodie."}
              </p>
            </div>
          )}

          {isCustomApparel && (
            <div data-testid="product-apparel-options" className="mb-8 space-y-6 border border-white/10 bg-[#141414] p-5">
              <p className="overline">Customise your hoodie</p>

              {/* Size */}
              <div>
                <label className="overline block mb-2">Size</label>
                <div className="grid grid-cols-6 gap-2">
                  {APPAREL_SIZES.map((s) => (
                    <button
                      key={s}
                      data-testid={`option-apparel-size-${s}`}
                      onClick={() => { setApparelSize(s); setViewMode("design"); }}
                      className={`py-2 text-sm font-display tracking-wider border transition-colors ${
                        apparelSize === s
                          ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                          : "border-white/20 text-white/80 hover:border-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hoodie colour */}
              <div>
                <label className="overline block mb-2">Hoodie colour</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {HOODIE_COLOURS.map((c) => (
                    <button
                      key={c.value}
                      data-testid={`option-hoodie-colour-${c.value}`}
                      onClick={() => { setHoodieColour(c.value); setViewMode("design"); }}
                      className={`flex items-center gap-2 p-2 text-xs uppercase tracking-widest border transition-colors ${
                        hoodieColour === c.value
                          ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                          : "border-white/20 text-white/80 hover:border-white"
                      }`}
                    >
                      <span className="w-5 h-5 rounded-full border" style={{ backgroundColor: c.swatch, borderColor: c.border }} />
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Name — buyer types their name, it becomes a selectable print */}
              <div data-testid="custom-name-section" className="border border-white/10 bg-[#0F0F0F] p-4">
                <label className="overline block mb-2">Put your name on it</label>
                <input
                  data-testid="custom-name-input"
                  type="text"
                  maxLength={22}
                  placeholder="Type your name…"
                  value={customName}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^a-zA-Z0-9 &.'\-!]/g, "").slice(0, 22);
                    setCustomName(v);
                    // auto-select the custom-name design when the user starts typing
                    if (v.trim() && printDesign !== CUSTOM_NAME_VALUE) {
                      setPrintDesign(CUSTOM_NAME_VALUE);
                    }
                    if (!v.trim() && printDesign === CUSTOM_NAME_VALUE && printDesigns[0]) {
                      setPrintDesign(printDesigns[0].value);
                    }
                    setViewMode("design");
                  }}
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white px-3 py-2.5 font-display tracking-wider text-lg placeholder:text-white/30 focus:outline-none focus:border-[#FF4500]"
                />
                <div className="mt-3">
                  <label className="overline block mb-2 text-[10px]">Font style</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {NAME_FONTS.map((f) => (
                      <button
                        key={f.value}
                        data-testid={`option-name-font-${f.value}`}
                        onClick={() => { setCustomFont(f.value); if (trimmedName) setPrintDesign(CUSTOM_NAME_VALUE); setViewMode("design"); }}
                        className={`px-2 py-2 text-xs border transition-colors truncate ${
                          customFont === f.value
                            ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                            : "border-white/20 text-white/80 hover:border-white"
                        }`}
                        style={{ fontFamily: f.family }}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mt-3">
                  Letters, numbers and simple punctuation only. Max 22 characters. Rendered in your chosen font colour.
                </p>
              </div>

              {/* Print design picker (managed via Admin) */}
              <div>
                <label className="overline block mb-2">Print design</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {displayDesigns.map((d) => (
                    <button
                      key={d.value}
                      data-testid={`option-print-design-${d.value}`}
                      onClick={() => { setPrintDesign(d.value); setViewMode("design"); }}
                      className={`flex items-center gap-3 p-3 text-left border transition-colors ${
                        printDesign === d.value
                          ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                          : "border-white/20 text-white/80 hover:border-white"
                      }`}
                    >
                      <span className="w-14 h-14 shrink-0 bg-white border border-white/10 overflow-hidden flex items-center justify-center">
                        {d.isCustomName ? (
                          <span className="text-black leading-none text-center px-1" style={{ fontFamily: nameFont.family, fontSize: Math.min(24, 130 / Math.max(4, trimmedName.length)) }}>{trimmedName}</span>
                        ) : (
                          <img src={d.preview} alt={d.label} className="w-full h-full object-contain" />
                        )}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-display tracking-wider text-sm">{d.label}</span>
                        <span className="block text-[10px] uppercase tracking-widest text-white/50 mt-0.5 truncate">{d.tag}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mt-3">
                  Every print is DTF-applied — same premium hand-cut finish across all five artworks.
                </p>
              </div>

              {/* Font colour — only applies to the single-colour brush/mono designs.
                  Tints just the ink (font), the surrounding is transparent. */}
              {isRecolourable ? (
                <div>
                  <label className="overline block mb-2">Font colour</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {LOGO_COLOURS.map((c) => (
                      <button
                        key={c.value}
                        data-testid={`option-logo-colour-${c.value}`}
                        onClick={() => { setLogoColour(c.value); setViewMode("design"); }}
                        className={`flex items-center gap-2 p-2 text-xs uppercase tracking-widest border transition-colors ${
                          logoColour === c.value
                            ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                            : "border-white/20 text-white/80 hover:border-white"
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full border" style={{ backgroundColor: c.swatch, borderColor: c.border }} />
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div data-testid="logo-colour-locked" className="border border-dashed border-white/15 bg-[#0A0A0A]/60 p-3 text-[11px] uppercase tracking-widest text-white/50">
                  Colour is baked into this emblem — prints exactly as shown.
                </div>
              )}

              {/* Placement note — actual position is set in the designer */}
              <div className="border border-dashed border-[#FF4500]/40 bg-[#FF4500]/5 p-3 text-[11px] uppercase tracking-widest text-[#FF4500]">
                Position & size are set in the <span className="font-display">Design your hoodie</span> tab above.
              </div>

              <p className="text-xs text-white/40 pt-2 border-t border-white/10">Every hoodie is DTF-printed to your spec. Turnaround 5–10 business days from payment. Pay by PayID — no card fees.</p>
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            <button data-testid="product-add-to-cart" onClick={addToCart} className="btn-primary">
              <ShoppingBag className="w-4 h-4" /> Add to cart
            </button>
            <button
              data-testid="product-buy-now"
              onClick={() => { addToCart(); navigate("/checkout"); }}
              className="btn-ghost"
            >
              Buy now
            </button>
          </div>
          <div className="mt-10 pt-8 border-t border-white/10 text-sm text-white/60 space-y-2">
            <p>· Ships direct from CKA — worldwide</p>
            <p>· 30-day returns on apparel · digital delivery instant</p>
            <p>· Pay by PayID — no card fees, secure bank transfer</p>
          </div>
          <div className="mt-6">
            <PaymentBadges />
          </div>
          <div className="mt-6">
            <button data-testid="product-checkout-now" onClick={() => { addToCart(); navigate("/checkout"); }} className="w-full text-center py-4 border border-white/20 hover:bg-white hover:text-black transition-colors font-display tracking-widest">
              Checkout now
            </button>
          </div>
        </div>
      </div>
      <ProductReviews productId={product.id} productTitle={product.title} />
      {zoom && (
        <div data-testid="product-image-lightbox" className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10" onClick={() => setZoom(false)}>
          <button data-testid="product-lightbox-close" onClick={() => setZoom(false)} className="absolute top-4 right-4 md:top-8 md:right-8 w-11 h-11 border border-white/20 bg-[#0A0A0A]/60 flex items-center justify-center hover:border-[#FF4500] hover:text-[#FF4500] z-10" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
          <img src={selectedImage || product.image_url} alt={product.title} className="max-w-[92vw] max-h-[88vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
